import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ============================================================================
// INTERFACES
// ============================================================================

export interface MatiereNested {
  id: number;
  code_matiere: string;
  nom_matiere: string;
  credits: number;
  coefficient_devoir: number;
  coefficient_examen: number;
}

export interface UniteEnseignement {
  id: number;
  code_ue: string;
  nom_ue: string;
  niveau: string;
  semestre: number;
  option: string;
  ordre_affichage: number;
  matieres: MatiereNested[];
  credits_total: number;
}

export interface UniteEnseignementList {
  id: number;
  code_ue: string;
  nom_ue: string;
  niveau: string;
  semestre: number;
  option: string;
  nb_matieres: number;
}

export interface UEFilters {
  niveau?: string;
  semestre?: number;
  option?: string;
}

export interface UECreate {
  code_ue: string;
  nom_ue: string;
  niveau: string;
  semestre: number;
  option: string;
  ordre_affichage?: number;
}

export interface CopierUEsParams {
  option_source: string;
  options_cibles: string[];
  niveau: string;
}

export interface CopierUEsResult {
  success: boolean;
  message: string;
  created_ues: number;
  created_matieres: number;
  skipped_ues: number;
}

export interface NettoyageFiltres {
  niveau: string;
  scope: 'option' | 'options' | 'parcours' | 'pole' | 'tout';
  option?: string;
  options_liste?: string[];
  parcours_id?: number;
  pole_id?: number;
}

export interface BackupMatiere {
  code_matiere: string;
  nom_matiere: string;
  credits: string;
  niveau: string;
  semestre: number;
  coefficient_devoir: string;
  coefficient_examen: string;
}

export interface BackupUE {
  code_ue: string;
  nom_ue: string;
  niveau: string;
  semestre: number;
  option: string;
  ordre_affichage: number;
  matieres: BackupMatiere[];
}

export interface NettoyageApercu {
  nb_ues: number;
  nb_matieres: number;
  nb_orphelines?: number;
  backup: BackupUE[];
  dependances?: Record<string, number>;
}

export interface NettoyageResult {
  success: boolean;
  message: string;
  nb_ues: number;
  nb_matieres: number;
}

// ============================================================================
// API SERVICE
// ============================================================================

export const uesApi = createApi({
  reducerPath: 'uesApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['UEs'],
  endpoints: (builder) => ({
    // Liste des UEs
    getUEs: builder.query<UniteEnseignementList[], UEFilters | void>({
      query: (params) => ({
        url: '/ues/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['UEs'],
    }),

    // Détail d'une UE (avec matières)
    getUE: builder.query<UniteEnseignement, number>({
      query: (id) => `/ues/${id}/`,
      providesTags: ['UEs'],
    }),

    // Créer une UE
    createUE: builder.mutation<UniteEnseignement, UECreate>({
      query: (data) => ({
        url: '/ues/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UEs'],
    }),

    // Modifier une UE
    updateUE: builder.mutation<UniteEnseignement, { id: number; data: Partial<UECreate> }>({
      query: ({ id, data }) => ({
        url: `/ues/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['UEs'],
    }),

    // Supprimer une UE
    deleteUE: builder.mutation<void, number>({
      query: (id) => ({
        url: `/ues/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UEs'],
    }),

    // Copier les UE/matières d'une option vers d'autres options
    copierUEsVersOption: builder.mutation<CopierUEsResult, CopierUEsParams>({
      query: (data) => ({
        url: '/ues/copier_vers_option/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UEs'],
    }),

    // Aperçu nettoyage (sans suppression, retourne backup JSON)
    apercuNettoyage: builder.mutation<NettoyageApercu, { filtres: NettoyageFiltres }>({
      query: (data) => ({
        url: '/ues/apercu_nettoyage/',
        method: 'POST',
        body: data,
      }),
    }),

    // Nettoyage (suppression réelle)
    nettoyage: builder.mutation<NettoyageResult, { filtres: NettoyageFiltres }>({
      query: (data) => ({
        url: '/ues/nettoyage/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UEs'],
    }),

    // Proposer une répartition LMD des matières en UEs (mode auto par défaut : charge actuel si existe)
    getPropositionRepartition: builder.query<PropositionRepartition, { niveau: string; semestre: number; option_id: number; mode?: 'auto' | 'actuel' | 'propose' }>({
      query: ({ niveau, semestre, option_id, mode }) =>
        `/ues/proposition_repartition/?niveau=${niveau}&semestre=${semestre}&option_id=${option_id}${mode ? `&mode=${mode}` : ''}`,
    }),

    // Appliquer une répartition UE → Matières
    appliquerRepartition: builder.mutation<AppliquerRepartitionResult, AppliquerRepartitionPayload>({
      query: (data) => ({
        url: '/ues/appliquer_repartition/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UEs'],
    }),

    // Détecter les options mutualisables d'un même niveau + semestre
    detecterMutualisations: builder.query<DetectionMutualisationResponse,
      { niveau: string; semestre: number; pole_code?: string; seuil?: number }>({
      query: ({ niveau, semestre, pole_code, seuil }) => {
        const params = new URLSearchParams({
          niveau, semestre: String(semestre),
        });
        if (pole_code) params.set('pole_code', pole_code);
        if (seuil != null) params.set('seuil', String(seuil));
        return `/ues/detecter-mutualisations/?${params.toString()}`;
      },
    }),

    // Proposition mutualisée pour un groupe d'options
    getPropositionMutualisee: builder.query<PropositionMutualiseeResponse,
      { niveau: string; semestre: number; options_ids: number[] }>({
      query: ({ niveau, semestre, options_ids }) =>
        `/ues/proposition-mutualisee/?niveau=${niveau}&semestre=${semestre}&options_ids=${options_ids.join(',')}`,
    }),

    // Proposition IA (nouvel algorithme : classification enrichie + regroupement LMD par profil)
    getPropositionRepartitionIA: builder.query<PropositionIAResponse,
      { niveau?: string; semestre?: number; option_id?: number; pole_code?: string } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.niveau) params.set('niveau', args.niveau);
        if (args?.semestre != null) params.set('semestre', String(args.semestre));
        if (args?.option_id != null) params.set('option_id', String(args.option_id));
        if (args?.pole_code) params.set('pole_code', args.pole_code);
        const qs = params.toString();
        return `/ues/proposition-repartition-ia/${qs ? '?' + qs : ''}`;
      },
      providesTags: ['UEs'],
    }),

    // Appliquer EN BLOC toutes les propositions IA (avec ou sans filtres)
    appliquerPropositionsIA: builder.mutation<{
      success: boolean; applied: number; skipped: number; errors: any[]; message: string;
    }, { filtres?: { niveau?: string; pole_code?: string; option_id?: number };
         propositions?: any[] }>({
      query: (body) => ({
        url: '/ues/appliquer-propositions-ia/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UEs'],
    }),

    // Copier la répartition UE d'une option vers d'autres options (sans toucher aux notes)
    syntheseRepartition: builder.query<SyntheseResponse, SyntheseFiltres | void>({
      query: (filtres) => {
        const p = new URLSearchParams();
        if (filtres) {
          Object.entries(filtres).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
          });
        }
        const qs = p.toString();
        return { url: `/ues/synthese-repartition/${qs ? `?${qs}` : ''}` };
      },
      providesTags: ['UEs'],
    }),

    copierRepartitionVersOptions: builder.mutation<CopierRepartitionResponse, CopierRepartitionPayload>({
      query: (body) => ({
        url: '/ues/copier-repartition-vers-options/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UEs'],
    }),
  }),
});

// ─── Types pour la Répartition LMD ─────────────────────────────────────────

export type TypeEnseignement = 'transversale' | 'fondamentale' | 'professionnelle';

export interface PropositionMatiere {
  id: number;
  code_matiere: string;
  nom_matiere: string;
  credits: number;
  coefficient_devoir: number;
  coefficient_examen: number;
  categorie: string;
  type_enseignement?: TypeEnseignement;
}

export interface PropositionUE {
  ordre: number;
  code_ue: string;
  nom_ue: string;
  est_tp: boolean;
  type_enseignement?: TypeEnseignement;
  categories: string[];
  matieres: PropositionMatiere[];
  credits_total: number;
  nb_matieres: number;
}

// ─── Types — Mutualisation d'options ─────────────────────────────────────────

export interface OptionMutualisable {
  id: number;
  code: string;
  nom: string;
  pole_nom: string;
  nb_matieres: number;
  nb_specifiques?: number;
}

export interface GroupeMutualisable {
  options: OptionMutualisable[];
  nb_matieres_communes: number;
  matieres_communes_ids: number[];
  matieres_specifiques_par_option: Record<string, number[]>;
  score_similarite: number;
  recommandation: 'mutualisation_forte' | 'mutualisation_partielle';
}

export interface DetectionMutualisationResponse {
  niveau: string;
  semestre: number;
  pole_code: string | null;
  seuil_similarite: number;
  groupes_mutualisables: GroupeMutualisable[];
  options_isolees: OptionMutualisable[];
}

export interface PropositionMutualiseeResponse {
  mode: 'mutualisee';
  options: Array<OptionMutualisable & {
    nb_matieres_total: number;
    nb_matieres_communes: number;
    nb_matieres_specifiques: number;
  }>;
  tronc_commun: PropositionRepartition;
  matieres_specifiques_par_option: Record<string, PropositionMatiere[]>;
  contexte: { niveau: string; semestre: number; pole_code: string };
}

export interface PropositionAlerte {
  type: 'success' | 'info' | 'warning' | 'error';
  message: string;
}

export interface PropositionRepartition {
  ues: PropositionUE[];
  non_classees: PropositionMatiere[];
  alertes: PropositionAlerte[];
  stats: {
    total_matieres: number;
    total_credits: number;
    nb_ues: number;
    nb_ues_non_tp: number;
    nb_non_classees: number;
    credits_cible_lmd: number;
  };
  contexte: {
    pole_code: string;
    niveau: string;
    semestre: number;
    option_code: string;
    option_nom: string;
  };
}

export interface AppliquerRepartitionPayload {
  niveau: string;
  semestre: number;
  option_id: number;
  ues: Array<{
    code_ue: string;
    nom_ue: string;
    ordre_affichage?: number;
    matiere_ids: number[];
  }>;
}

export interface AppliquerRepartitionResult {
  success: boolean;
  message: string;
  ues_ids: number[];
  delta?: {
    ues_creees: number;
    ues_mises_a_jour: number;
    ues_supprimees: number;
    matieres_deplacees: number;
  };
  recalcul_cs_recommande?: boolean;
  option_nom?: string;
}

// ─── Types pour la proposition IA (nouvel algorithme) ──────────────────────
export interface MatiereIA {
  id: number;
  nom: string;
  code?: string;
  credits: number | null;
  famille: string | null;
  famille_label: string | null;
  nature: 'UEF' | 'UEM' | 'UET' | 'UED' | null;
  ue_actuelle_id?: number | null;
  ue_actuelle_nom?: string | null;
}

export interface UEProposeeIA {
  ordre: number;
  nom_suggere: string;
  nature: 'UEF' | 'UEM' | 'UET' | 'UED';
  nb_matieres: number;
  credits: number;
  credits_suggeres?: number;  // crédits proposés pour totaliser 30 (norme LMD)
  is_tp?: boolean;            // true si c'est l'UE Travaux Pratiques (séparée par design)
  matieres: MatiereIA[];
}

export interface PropositionClasseIA {
  option_id: number;
  option_nom: string;
  option_code: string;
  pole_code: string | null;
  pole_nom: string | null;
  niveau: string;
  semestre: number;
  profil: 'SCIENTIFIQUE' | 'COMMERCIAL' | 'JURIDIQUE';
  total_matieres: number;
  total_credits: number;
  total_credits_suggeres?: number;  // somme des credits_suggeres = 30 garanti
  nb_ues: number;
  ues: UEProposeeIA[];
  non_placees_count: number;
}

export interface PropositionIAResponse {
  total_classes: number;
  propositions: PropositionClasseIA[];
  stats: {
    nb_ues_distribution: Record<string, number>;
    non_placees_total: number;
  };
}

// ─── Types pour la copie de répartition entre options ────────────────────
export interface CopierRepartitionPayload {
  option_source_id: number;
  options_cibles_ids: number[];
  niveau: string;
  semestre: number;
  dry_run?: boolean;
}

export interface CopierRepartitionResumeOption {
  option_id: number;
  option_nom: string;
  nb_ues: number;
  nb_matieres_apparies: number;
  non_apparies: Array<{
    matiere_source_code: string;
    matiere_source_nom: string;
    raison: string;
  }>;
}

export interface CopierRepartitionResponse {
  success?: boolean;
  dry_run?: boolean;
  message?: string;
  option_source?: { id: number; nom: string };
  resume_par_option: CopierRepartitionResumeOption[];
}

// ─── Synthèse de la répartition (contrôle académique) ───────────────────────
export interface SyntheseMatiere {
  id: number;
  code: string;
  nom: string;
  credits: number;
}
export interface SyntheseUE {
  id: number;
  code_ue: string;
  nom_ue: string;
  ordre: number;
  credits: number;
  matieres: SyntheseMatiere[];
}
export interface SyntheseGroupe {
  option: string;
  pole: string;
  niveau: string;
  semestre: number;
  effectif: number;
  total_credits: number;
  ecart: number;
  conforme: boolean;
  nb_ues: number;
  nb_matieres: number;
  ues: SyntheseUE[];
}
export interface SyntheseResponse {
  cible: number;
  groupes: SyntheseGroupe[];
  stats: {
    nb_groupes: number;
    nb_conformes: number;
    nb_ecarts: number;
    nb_ues: number;
    nb_matieres: number;
    effectif_total: number;
    poles: string[];
    options: string[];
  };
}
export interface SyntheseFiltres {
  niveau?: string;
  semestre?: number | string;
  pole?: string;
  option?: string;
  type_formation?: string;
  conformite?: 'ok' | 'ecart' | '';
}

export const {
  useGetUEsQuery,
  useGetUEQuery,
  useCreateUEMutation,
  useUpdateUEMutation,
  useDeleteUEMutation,
  useCopierUEsVersOptionMutation,
  useApercuNettoyageMutation,
  useNettoyageMutation,
  useGetPropositionRepartitionQuery,
  useLazyGetPropositionRepartitionQuery,
  useAppliquerRepartitionMutation,
  useDetecterMutualisationsQuery,
  useLazyDetecterMutualisationsQuery,
  useGetPropositionMutualiseeQuery,
  useLazyGetPropositionMutualiseeQuery,
  useGetPropositionRepartitionIAQuery,
  useAppliquerPropositionsIAMutation,
  useCopierRepartitionVersOptionsMutation,
  useSyntheseRepartitionQuery,
} = uesApi;
